import Image from 'next/image';
import { HTMLAttributeAnchorTarget } from 'react';

import { useTrackEventAmplitude } from '../../../../core/Tracking/Amplitude/Amplitude';
import { IconInstagram } from '../../../../design-system/components/icons/Instagram';
import { IconLinkedIn } from '../../../../design-system/components/icons/Linkedin';
import { Typography } from '../../../../design-system/components/typography/Typography';
import { HtmlContent } from '../../../../domains/Layout/HtmlContent/HtmlContent';
import { imageLoaderForPicture } from '../../../../domains/Layout/Image/URL/ImageURL';
import { Author } from '../../../../resources/author/domain/types/author';
import { useUrlGenerator } from '../../../hooks/useUrlGenerator';
import { PickDomain } from '../../../utilities/types/PickDomain';

import style from './AuthorBanner.module.css';

type AuthorBannerProps = {
    author: PickDomain<
        Author,
        'id' | 'firstName' | 'lastName' | 'picture' | 'role' | 'description' | 'slug',
        {
            linkedinUrl: Author['linkedinUrl'] | null;
            instagramUrl: Author['instagramUrl'] | null;
        }
    >;
    target?: HTMLAttributeAnchorTarget;
};

export function AuthorBanner({ author, target }: AuthorBannerProps) {
    const { trackEvent } = useTrackEventAmplitude();
    const { generatePath } = useUrlGenerator();

    return (
        <div className={style.card}>
            <div className={style.pictureWrapper}>
                <Image
                    className={style.picture}
                    src={author.picture.id}
                    alt={author.firstName}
                    loader={imageLoaderForPicture(author.picture, {
                        aspectRatio: 1 / 1,
                    })}
                    sizes="200px"
                    fill
                    loading="lazy"
                />
            </div>

            <div className={style.content}>
                <div className={style.contentHeader}>
                    <div className={style.nameAndSocialLinks}>
                        <a
                            className={style.link}
                            onClick={() => {
                                trackEvent((ampli, defaultProperties) => {
                                    ampli.authorClicked({
                                        ...defaultProperties,
                                        block: 'author',
                                    });
                                });
                            }}
                            href={generatePath('author', {
                                authorId: author.id,
                                authorSlug: author.slug,
                            })}
                            target={target}
                        >
                            <Typography className={style.authorName} scale="primary-s-extra-bold">
                                {author.firstName} {author.lastName}
                            </Typography>
                        </a>

                        {author.instagramUrl || author.linkedinUrl ? (
                            <div className={style.socialMediaList}>
                                {author.instagramUrl ? (
                                    <div className={style.socialMediaItem}>
                                        <a
                                            href={author.instagramUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <IconInstagram color="var(--color-green-700)" />
                                        </a>
                                    </div>
                                ) : null}
                                {author.linkedinUrl ? (
                                    <div className={style.socialMediaItem}>
                                        <a
                                            href={author.linkedinUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <IconLinkedIn color="var(--color-green-700)" />
                                        </a>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    <Typography scale="primary-body-bold">{author.role}</Typography>
                </div>
                <HtmlContent className={style.description} htmlContent={author.description} />
            </div>
        </div>
    );
}
